<template>
  <div
    :is="tag"
    v-if="resource && resource.columns"
    :class="classes"
    :data-resource="resource.id"
    @click.stop="handleClick"
  >
    <div
      v-if="cmsEditable && !resource.locked && !resource.resource_class_name.endsWith('Page')"
      class="cms-component__edit"
      @click.stop="handleEdit"
    >
      Bearbeiten
    </div>
    <slot />
  </div>
</template>

<script>
import { BaseResource as Resource } from '../defaults/resource'
import editable from '../mixins/editable'
import withMessages from '../mixins/with-messages'

export default {
  mixins: [editable, withMessages],
  props: {
    tag: {
      type: String,
      default: () => 'div'
    }
  },
  computed: {
    cmsEnabled () {
      return !!this.$cms
    },
    cmsEditable () {
      return this.cmsEnabled && this.$cms.editable
    },
    isDirty () {
      if (!this.cmsEnabled) { return false }
      return this.hasDirtyResource(this.resource)
    },
    classes () {
      return ['block-component', {
        'cms-component': this.cmsEnabled,
        'cms-component--dirty': this.cmsEnabled && !this.resource.url && this.isDirty,
        'cms-component--clean': this.cmsEnabled && !this.resource.url && !this.isDirty,
        'cms-component--highlighted': this.cmsEnabled && this.resource.highlighted,
        'cms-component--locked': this.cmsEnabled && this.resource.locked
      }, [
        this.resource.columns.marginTop && `cms-component--margin-top-${this.resource.columns.marginTop.toLowerCase()}`,
        this.resource.columns.marginBottom && `cms-component--margin-bottom-${this.resource.columns.marginBottom.toLowerCase()}`
      ]]
    }
  },
  methods: {
    hasDirtyResource (resource) {
      if (resource.dirty) {
        return true
      }

      if (resource.children && resource.children.length) {
        return !!resource.children.find(child => this.hasDirtyResource(child))
      }

      return false
    },
    handleEdit () {
      this.openEditForm()
    },
    handleClick () {
      if (this.cmsEnabled) {
        if (process.browser) {
          // Fix active editor blur bug on selecting the same CMS component
          const tinymce = require('tinymce/tinymce')
          if (tinymce.activeEditor) {
            tinymce.activeEditor.fire('blur')
          }
        }

        if (!this.resource.highlighted) {
          Resource.update({
            where: r => r.highlighted,
            data: {
              highlighted: false
            }
          })

          Resource.update({
            where: this.resource.id,
            data: {
              highlighted: true
            }
          })

          this.sendResourceSelectedMessage(this.resource)
        }
      }
    }
  }
}
</script>

<style lang="scss">
$cms-colors: (
  default: #999,
  active: #ffe650,
  edited: #f57c00
);

.block-component {
  border-bottom: 0.001rem solid transparent;
}

.cms-component {
  position: relative;
  outline: 3px dashed gray;
  transition: filter 0.5s ease-in-out;

  &__edit {
    cursor: pointer;
    user-select: none;
    letter-spacing: letter-spacing(solid);
    font-size: font-size(2);
    position: absolute;
    top: 0;
    right: 0;
    background-color: #333;
    color: #fff;
    font-size: 12px;
    text-align: center;
    padding: 5px 15px;
    z-index: 20;
    transition: background-color 0.2s / 2 ease;

    &:hover {
      background-color: #222;
    }
  }

  &--margin-top-s {
    margin-top: 30px;
  }

  &--margin-top-m {
    margin-top: 90px;
  }

  &--margin-top-l {
    margin-top: 180px;
  }

  &--margin-bottom-s {
    margin-bottom: 30px;
  }

  &--margin-bottom-m {
    margin-bottom: 90px;
  }

  &--margin-bottom-l {
    margin-bottom: 180px;
  }
}

.cms--changes-visible {
  .cms-component--clean {
    filter: grayscale(1) blur(5px);
    pointer-events: none;
  }

  .cms-component--dirty {
    position: relative;
    &:before {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      box-shadow: inset 0 0 0 3px yellow;
      z-index: 5;
    }
  }
}

.cms-component--highlighted {
  box-shadow: inset 0 0 0 5px yellow;
}

.cms-component--locked {
    position: relative;
    &:before {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      backdrop-filter: grayscale(1);
      background-color: rgba(255,255,255,0.5);
      z-index: 10;
    }
}

.cms-field {
  outline-width: 2px;
  outline-color: color(default, $cms-colors);

  &.medium-editor-element {
    display: inline-block;
  }

  &.medium-editor-placeholder {
    color: #EEE;
  }

  &:hover {
    outline-style: solid;
  }

  &--edited {
    outline-color: color(edited, $cms-colors);
    outline-style: solid;
  }

  &--focused {
    outline-width: 4px;
    outline-style: solid;

    &.cms-field--edited {
      outline-color: color(active, $cms-colors)
    }
  }
}
</style>
